import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Minus, PieChart as PieIcon } from 'lucide-react';

interface PlatformData {
  platform: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number;
  roas: number;
  color: string;
}

interface PlatformComparisonProps {
  data: PlatformData[];
  loading?: boolean;
  metric?: 'spend' | 'impressions' | 'clicks' | 'conversions';
}

const PLATFORM_ICONS: Record<string, string> = {
  'Google Ads': '🔍',
  'Meta Ads': '📘',
  'LinkedIn Ads': '💼',
  'Twitter Ads': '🐦',
  'TikTok Ads': '🎵'
};

const PlatformComparison: React.FC<PlatformComparisonProps> = ({
  data,
  loading = false,
  metric = 'spend'
}) => {
  const { t } = useTranslation();

  const getTrendIcon = (value: number) => {
    if (value > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (value < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const formatValue = (value: number, type: string) => {
    switch (type) {
      case 'spend':
        return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
      case 'impressions':
        return value >= 1000000
          ? `${(value / 1000000).toFixed(1)}M`
          : value >= 1000
          ? `${(value / 1000).toFixed(1)}K`
          : value.toString();
      case 'clicks':
        return value >= 1000
          ? `${(value / 1000).toFixed(1)}K`
          : value.toString();
      case 'ctr':
        return `${value.toFixed(2)}%`;
      case 'cpc':
        return `R$ ${value.toFixed(2)}`;
      case 'roas':
        return `${value.toFixed(2)}x`;
      default:
        return value.toLocaleString('pt-BR');
    }
  };

  const pieData = data.map(d => ({
    name: d.platform,
    value: d[metric],
    color: d.color
  }));

  const total = data.reduce((sum, d) => sum + d[metric], 0);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4 animate-pulse" />
        <div className="flex">
          <div className="w-1/2">
            <div className="h-48 bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse" />
          </div>
          <div className="w-1/2 space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Nada gasto ainda: um aviso curto no lugar de um gráfico de pizza vazio
  if (!data.length || total <= 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 h-full">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('dashboard.platformComparison', 'Comparação de Plataformas')}
        </h3>
        <div className="flex flex-col items-center justify-center text-center py-8">
          <PieIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
            {t('dashboard.platformEmpty', 'Quando você tiver anúncios no Google ou no Meta, aqui aparece onde o dinheiro está rendendo mais.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t('dashboard.platformComparison', 'Comparação de Plataformas')}
      </h3>

      <div className="flex flex-col lg:flex-row">
        {/* Pie Chart */}
        <div className="w-full lg:w-1/2 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => formatValue(value, metric)}
                contentStyle={{
                  backgroundColor: 'var(--tooltip-bg, #fff)',
                  borderColor: 'var(--tooltip-border, #e5e7eb)'
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Platform Details */}
        <div className="w-full lg:w-1/2 mt-4 lg:mt-0 space-y-3">
          {data.map((platform) => (
            <div
              key={platform.platform}
              className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{PLATFORM_ICONS[platform.platform] || '📊'}</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {platform.platform}
                  </span>
                </div>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${platform.color}20`, color: platform.color }}
                >
                  {((platform[metric] / total) * 100).toFixed(1)}%
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="min-w-0">
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('campaigns.metrics.spend', 'Gasto')}
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {formatValue(platform.spend, 'spend')}
                  </p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('campaigns.metrics.clicks', 'Cliques')}
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {formatValue(platform.clicks, 'clicks')}
                  </p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('campaigns.metrics.ctr', 'CTR')}
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {formatValue(platform.ctr, 'ctr')}
                  </p>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('campaigns.metrics.roas', 'ROAS')}
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {formatValue(platform.roas, 'roas')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformComparison;
